import type { Map as MapboxMap } from "mapbox-gl";
import { useEffect, useRef } from "react";

const ADDRESSES_LAYER_ID = "addresses-points";

export interface UseBoxSelectOptions {
    map: MapboxMap | null;
    enabled: boolean;
    onSelection: (addressIds: string[]) => void;
}

/**
 * Rectangle drag selection on the map. When enabled, dragging on the map canvas
 * draws a box and selects all visible address points inside it.
 */
export function useBoxSelect({ map, enabled, onSelection }: UseBoxSelectOptions): void {
    const onSelectionRef = useRef(onSelection);
    onSelectionRef.current = onSelection;

    useEffect(() => {
        if (!map || !enabled) {
            return;
        }

        const container = map.getCanvasContainer();
        let startPoint: { x: number; y: number } | null = null;
        let boxEl: HTMLDivElement | null = null;
        let isDrawing = false;

        const createBoxElement = (): HTMLDivElement => {
            const el = document.createElement("div");
            el.className = "map-box-select-rect";
            container.appendChild(el);
            return el;
        };

        const updateBoxElement = (start: { x: number; y: number }, current: { x: number; y: number }) => {
            if (!boxEl) {
                return;
            }
            const left = Math.min(start.x, current.x);
            const top = Math.min(start.y, current.y);
            const width = Math.abs(current.x - start.x);
            const height = Math.abs(current.y - start.y);
            boxEl.style.left = `${left}px`;
            boxEl.style.top = `${top}px`;
            boxEl.style.width = `${width}px`;
            boxEl.style.height = `${height}px`;
        };

        const removeBoxElement = () => {
            if (boxEl) {
                boxEl.remove();
                boxEl = null;
            }
        };

        const finishSelection = (end: { x: number; y: number }) => {
            if (!startPoint) {
                return;
            }

            const minX = Math.min(startPoint.x, end.x);
            const minY = Math.min(startPoint.y, end.y);
            const maxX = Math.max(startPoint.x, end.x);
            const maxY = Math.max(startPoint.y, end.y);

            if (maxX - minX >= 4 && maxY - minY >= 4) {
                const features = map.queryRenderedFeatures(
                    [
                        [minX, minY],
                        [maxX, maxY]
                    ],
                    { layers: [ADDRESSES_LAYER_ID] }
                );

                const ids = new Set<string>();
                for (const feature of features) {
                    const id = feature.properties?.addressPointId as string | undefined;
                    if (id) {
                        ids.add(id);
                    }
                }

                if (ids.size > 0) {
                    onSelectionRef.current(Array.from(ids));
                }
            }

            startPoint = null;
            isDrawing = false;
            removeBoxElement();
            map.dragPan.enable();
            map.getCanvas().style.cursor = "crosshair";
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) {
                return;
            }
            const target = e.target as HTMLElement;
            if (!map.getCanvas().contains(target)) {
                return;
            }
            if (target.closest(".mapboxgl-marker, .mapboxgl-popup, .mapboxgl-ctrl")) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const rect = map.getCanvas().getBoundingClientRect();
            startPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top };
            isDrawing = true;
            boxEl = createBoxElement();
            updateBoxElement(startPoint, startPoint);
            map.dragPan.disable();
            map.getCanvas().style.cursor = "crosshair";
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isDrawing || !startPoint) {
                return;
            }
            const rect = map.getCanvas().getBoundingClientRect();
            const current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
            updateBoxElement(startPoint, current);
        };

        const onMouseUp = (e: MouseEvent) => {
            if (!isDrawing || !startPoint) {
                return;
            }
            const rect = map.getCanvas().getBoundingClientRect();
            const end = { x: e.clientX - rect.left, y: e.clientY - rect.top };
            finishSelection(end);
        };

        map.getCanvas().style.cursor = "crosshair";
        container.addEventListener("mousedown", onMouseDown, true);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);

        return () => {
            container.removeEventListener("mousedown", onMouseDown, true);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            removeBoxElement();
            map.dragPan.enable();
            map.getCanvas().style.cursor = "";
        };
    }, [map, enabled]);
}
